// Date + format utilities. Verbatim extract from main.jsx.

// Anything the Date constructor accepts. A plain 'YYYY-MM-DD' string is the
// common case and is handled separately by addDays, which pins it to local noon
// so a DST shift can never move the result onto the adjacent day.
export type DateInput = string | number | Date;

// The formatters below are display helpers: they take an ISO date OR a full ISO
// timestamp, and they must survive the null/empty column values that reach them
// straight out of a database row — every one of them renders the em-dash
// placeholder rather than throwing.
export type ISODateInput = string | null | undefined;

export function addDays(dateOrISO: DateInput, n: number): Date {
  const d = typeof dateOrISO === 'string' ? new Date(dateOrISO + 'T12:00:00') : new Date(dateOrISO);
  d.setDate(d.getDate() + n);
  return d;
}
export function toISO(d: DateInput): string {
  return new Date(d).toISOString().split('T')[0];
}
// Canonical calendar display = mm/dd/yy (2-digit month / 2-digit day / 2-digit
// year). Applied site-wide for any ISO date the user sees. Ronnie's request
// 2026-04-23. Accepts either a plain date string (YYYY-MM-DD) or a full ISO
// timestamp (YYYY-MM-DDTHH:MM:...) -- only the date portion is used.
export function fmt(iso: ISODateInput): string {
  if (!iso) return '—';
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '—';
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  const yy = String(y).slice(-2);
  return mm + '/' + dd + '/' + yy;
}
// Full 4-digit variant (mm/dd/yyyy) for record-page titles + daily sequence
// labels where the explicit year is wanted.
export function fmtMDY(iso: ISODateInput): string {
  if (!iso) return '—';
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '—';
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return mm + '/' + dd + '/' + String(y).padStart(4, '0');
}
// Short variant drops the year for compact tiles; still mm/dd.
export function fmtS(iso: ISODateInput): string {
  if (!iso) return '—';
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return '—';
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return mm + '/' + dd;
}
export function todayISO(): string {
  return toISO(new Date());
}

// "Today" as a YYYY-MM-DD string in America/Chicago (Central Time).
//
// Tasks are date-only / Central time per Ronnie's lock — overdue and
// due-today comparisons must not flip just because a user opens the
// app on a phone whose timezone is set to UTC, Mountain, or anywhere
// else. Implementation uses Intl.formatToParts so we never construct
// an intermediate Date in the browser's local zone (which would
// reintroduce the same drift this helper exists to prevent).
//
// Caller passes an optional Date (or epoch ms) for testability;
// production callers can omit and get new Date().
export function todayCentralISO(now: DateInput = new Date()): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(now instanceof Date ? now : new Date(now));
  const get = (type: Intl.DateTimeFormatPartTypes) => {
    const p = parts.find((x) => x.type === type);
    return p ? p.value : '';
  };
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Same Central-time YYYY-MM-DD as todayCentralISO, but for any input
// timestamp instead of "now". Used by the Tasks Center Completed tab
// when bucketing rows by completed_at into Today / Last 7 days / Older
// against today's Central date — comparing the row's UTC slice would
// flip rows around UTC midnight (e.g. a 9:00 PM Central completion lands
// on the next UTC day and would silently jump bucket).
//
// Accepts the same inputs as fmtCentralDateTime: ISO string, Date, or
// epoch ms. Returns '' for null/undefined/empty so the caller can
// distinguish "no timestamp" from a parseable timestamp.
export function centralISOFor(input: DateInput | null | undefined): string {
  if (input === null || input === undefined || input === '') return '';
  const d = input instanceof Date ? input : new Date(input);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => {
    const p = parts.find((x) => x.type === type);
    return p ? p.value : '';
  };
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Format a timestamptz (ISO string, Date, or epoch ms) as
//   "mm/dd/yy h:mm AM/PM" in America/Chicago.
//
// Used by the Task Center's Completed tab so completed_at displays
// in farm time regardless of the viewer's browser timezone (Tasks
// are Central-time per Ronnie's lock — same reason todayCentralISO
// exists). Returns "—" for null/empty/malformed input.
export function fmtCentralDateTime(
  input: DateInput | null | undefined,
  {fullYear = false}: {fullYear?: boolean} = {},
): string {
  if (input === null || input === undefined || input === '') return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const dateFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: fullYear ? 'numeric' : '2-digit',
    month: '2-digit',
    day: '2-digit',
  });
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateFmt.format(d)} ${timeFmt.format(d)}`;
}

// The time half of fmtCentralDateTime on its own: "h:mm AM/PM" in
// America/Chicago, for compact time columns where the date is already shown
// (weigh-in entry rows). Same options, same "—" for null/empty/malformed input.
export function fmtCentralTime(input: DateInput | null | undefined): string {
  if (input === null || input === undefined || input === '') return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Read-only dates (Design Constitution §5: C13, T03, T04, T06) ────────────
// The one display format for dates people read: 'Oct 6' in the current year
// (farm Central time), 'Oct 6, 2025' in any other year. A date-time adds
// ' · 2:14 PM' in Central time. Native date inputs keep the browser format, and
// CSV/print files keep ISO dates (Q32). A missing value reads '—'.
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtDate(iso: ISODateInput, {now = new Date()}: {now?: DateInput} = {}): string {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d || m > 12 || d > 31) return '—';
  const thisYear = Number(todayCentralISO(now).slice(0, 4));
  const day = MONTHS_SHORT[m - 1] + ' ' + d;
  return y === thisYear ? day : day + ', ' + y;
}

export function fmtDateTime(input: DateInput | null | undefined, {now = new Date()}: {now?: DateInput} = {}): string {
  const day = centralISOFor(input);
  if (!day) return '—';
  return fmtDate(day, {now}) + ' \u00b7 ' + fmtCentralTime(input);
}

// ── HOLIDAY LOGIC ──────────────────────────────────────────────────────────
